import { useIntl } from "react-intl";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  createOneTelegramBotConfig,
  deleteOneTelegramBotConfig,
  getAllTelegramBotConfig,
  toggleTelegramBotConfig,
  updateOneTelegramBotConfig,
} from "@lib/api/telegramBotConfigs";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import getColumns from "./colums";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import { useEffect, useMemo, useState } from "react";
import { TableHeaderFilter } from "@lib/types/Api";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import { TelegramBotConfig } from "@lib/types/TelegramChatBot";
import { useNavigate } from "react-router-dom";
import QuickDrawer from "./QuickDrawer";
import { toast } from "react-toastify";
// import { AppConfigNames } from "@lib/types";
import { AppsContainer } from "@lib/components/AppComponents";

// const configName = AppConfigNames.telegramBotConfig;

const TelegramBotConfigs = () => {
  const { messages } = useIntl();
  const navigate = useNavigate();

  const { params, handleRemoveFilters, onPageChange } = useParamsManager({
    page: 1,
    limit: 100,
    sort: "createdAt,DESC",
  });

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selected, setSelected] = useState<TelegramBotConfig>();

  useEffect(() => {
    if (!openDrawer) setSelected(null);
  }, [openDrawer]);

  const toggleDrawer = (status?: boolean) => {
    if (typeof status !== "undefined") setOpenDrawer(status);
    else setOpenDrawer(!openDrawer);
  };

  const handleSelect = (value: TelegramBotConfig) => {
    setSelected(value);
    toggleDrawer(true);
  };

  const telegramBotConfigsQuery = useQuery({
    queryKey: ["telegram-bot-config"],
    queryFn: () => getAllTelegramBotConfig(params),
    enabled: !!params,
  });
  const { data, isFetching, isLoading, refetch } = telegramBotConfigsQuery;
  const apiData = data?.data?.data;

  const telegramBotConfigsCreateQuery = useMutation({
    mutationFn: (values: TelegramBotConfig) =>
      createOneTelegramBotConfig(values),
    onSuccess: () => {
      refetch();
      toast.success(
        (messages["common.createdMessage"] as string).replace(
          "{name}",
          messages["common.telegramBotConfig"] as string,
        ),
      );
    },
  });

  const telegramBotConfigsUpdateQuery = useMutation({
    mutationFn: (values: Pick<TelegramBotConfig, "id">) =>
      updateOneTelegramBotConfig({ id: values.id, payload: values }),
    onSuccess: () => {
      refetch();
      toast.success(
        (messages["common.updatedMessage"] as string).replace(
          "{name}",
          messages["common.telegramBotConfig"] as string,
        ),
      );
      telegramBotConfigsQuery.refetch();
    },
    onError: () => {
      toast.error(messages["common.error"] as string);
    },
  });

  const telegramBotConfigsToggleQuery = useMutation({
    mutationFn: (values: Pick<TelegramBotConfig, "id" | "enabled">) =>
      toggleTelegramBotConfig({
        id: values.id,
        payload: { enabled: !!values.enabled },
      }),
    onSuccess: () => {
      refetch();
      toast.success(
        (messages["common.updatedMessage"] as string).replace(
          "{name}",
          messages["common.telegramBotConfig"] as string,
        ),
      );
      telegramBotConfigsQuery.refetch();
    },
    onError: () => {
      toast.error(messages["common.error"] as string);
    },
  });

  const telegramBotConfigsDeleteQuery = useMutation({
    mutationFn: (id: string) => deleteOneTelegramBotConfig(id),
    onSuccess: () => {
      refetch();
      toast.success(
        (messages["common.deletedMessage"] as string).replace(
          "{name}",
          messages["common.telegramBotConfig"] as string,
        ),
      );
      telegramBotConfigsQuery.refetch();
    },
    onError: () => {
      toast.error(messages["common.error"] as string);
    },
  });

  const handleEditTelegramBotConfig = (
    values: Pick<TelegramBotConfig, "id" | "enabled">,
    enabled?: boolean,
  ) => {
    if (typeof enabled !== "undefined") {
      // console.log("🚀 ~ TelegramBotConfigs ~ values:", values);
      telegramBotConfigsToggleQuery.mutate({ ...values, enabled });
    } else {
      telegramBotConfigsUpdateQuery.mutate({ ...values });
    }
  };

  const handleOnSubmit = (formValues: TelegramBotConfig) => {
    telegramBotConfigsCreateQuery.mutate(formValues);
  };

  const columns = getColumns({
    messages,
    navigate,
    handleSelect,
    handleEditTelegramBotConfig,
    deleteQuery: telegramBotConfigsDeleteQuery,
  });

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter((f) => f.key !== "walletId") as TableHeaderFilter[];
  }, [params]);

  return (
    <AppsContainer
      fullView={true}
      cardBg
      title={messages["sidebar.telegramBotConfig"] as string}
      cardProps={{
        borderFull: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 100}
          onPageChange={onPageChange}
          query={telegramBotConfigsQuery}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          plusProps={{
            onClick: () => toggleDrawer(),
            disabled: isLoading,
            loading: isLoading,
          }}
        />
      </AppsHeader>

      <AppsContent>
        <AppTable<TelegramBotConfig>
          dataSource={apiData}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
        />
      </AppsContent>

      <QuickDrawer
        onCloseFunc={() => toggleDrawer(false)}
        visibleState={openDrawer}
        submitLoading={telegramBotConfigsUpdateQuery.isPending}
        onNewSubmit={handleOnSubmit}
        handleEditTelegramBotConfig={handleEditTelegramBotConfig}
        selectedTelegramBotConfig={selected}
      />
    </AppsContainer>
  );
};

export default TelegramBotConfigs;
